import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchCurrentSubscription,
  selectCurrentPlan,
  selectUsageStats,
  selectSubscriptionLoading,
  selectSubscriptionError
} from '@store/slices/subscriptionSlice';
import { selectUser } from '@store/slices/authSlice';
import {
  canAccessCategory,
  canAccessSubcategory,
  filterCategories,
  filterSubcategories,
  getCategoryRestrictionInfo,
  getSubcategoryRestrictionInfo,
  hasFullAccess,
  getAccessLevelDescription
} from '@utils/careerMapRestrictions';
import { isApiBlocked } from '@utils/apiRetry';
import { showApiErrorNotification } from '@utils/apiErrorNotification';

// Global flag to prevent multiple simultaneous API calls across all components
let globalFetchInProgress = false;
let globalHasFetched = false;
let globalHasNotified = false;

export const usePlanRestrictions = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const currentPlan = useSelector(selectCurrentPlan);
  const usageStats = useSelector(selectUsageStats);
  const loading = useSelector(selectSubscriptionLoading);
  const error = useSelector(selectSubscriptionError);
  const hasFetchedRef = useRef(false);
  const hasNotifiedRef = useRef(false);

  useEffect(() => {
    const apiKey = 'getCurrentPlan';
    const isBlocked = isApiBlocked(apiKey);
    
    // Use global flag to prevent multiple components from triggering simultaneous calls
    if (user?.role === 'student' && !currentPlan && !loading && !globalFetchInProgress && !globalHasFetched && !isBlocked) {
      const shouldFetch = !error || (error && !['NETWORK_ERROR', 'SERVER_ERROR', 'MAX_RETRIES_EXCEEDED', 'ACCESS_FORBIDDEN'].includes(error.error));
      
      if (shouldFetch) {
        globalFetchInProgress = true;
        globalHasFetched = true;
        hasFetchedRef.current = true;
        dispatch(fetchCurrentSubscription()).finally(() => {
          globalFetchInProgress = false;
        });
      }
    }
    
    if (isBlocked && error && !globalHasNotified && !hasNotifiedRef.current) {
      showApiErrorNotification(apiKey, error.error);
      globalHasNotified = true;
      hasNotifiedRef.current = true;
    }
    
    // Reset global flags when plan is loaded
    if (currentPlan) {
      globalHasFetched = false;
      globalHasNotified = false;
      hasFetchedRef.current = false;
      hasNotifiedRef.current = false;
    }
  }, [user?.role, currentPlan, loading, error, dispatch]);

  const refreshPlanData = useCallback(() => {
    // Only refresh plan data for students
    if (user?.role === 'student') {
      dispatch(fetchCurrentSubscription());
    }
  }, [dispatch, user?.role]);

  const canAccessFeature = useCallback((featureName) => {
    if (!currentPlan) return false;
    
    const features = currentPlan.features || {};
    return features[featureName] === true;
  }, [currentPlan]);

  const canCreateCareerMap = useCallback(() => {
    if (!currentPlan || !usageStats) return false;
    
    const maxMaps = currentPlan.maxCareerMaps;
    const currentMaps = usageStats.careerMapsCreated || 0;
    
    if (maxMaps === -1) return true;
    
    return currentMaps < maxMaps;
  }, [currentPlan, usageStats]);

  const canCreatePortfolio = useCallback(() => {
    if (!currentPlan || !usageStats) return false;
    
    const maxPortfolios = currentPlan.maxPortfolios;
    const currentPortfolios = usageStats.portfoliosCreated || 0;
    
    if (maxPortfolios === -1) return true;
    
    return currentPortfolios < maxPortfolios;
  }, [currentPlan, usageStats]);

  const canAccessProfileType = useCallback((profileType) => {
    if (!currentPlan) return false;
    
    const allowedTypes = currentPlan.allowedProfileTypes || [];
    return allowedTypes.includes(profileType);
  }, [currentPlan]);

  const hasMentorCredits = useCallback(() => {
    if (!currentPlan || !usageStats) return false;
    
    const totalCredits = currentPlan.mentorCredits || 0;
    const usedCredits = usageStats.mentorCreditsUsed || 0;
    
    return (totalCredits - usedCredits) > 0;
  }, [currentPlan, usageStats]);

  const getRemainingMentorCredits = useCallback(() => {
    if (!currentPlan || !usageStats) return 0;
    
    const totalCredits = currentPlan.mentorCredits || 0;
    const usedCredits = usageStats.mentorCreditsUsed || 0;
    
    return Math.max(0, totalCredits - usedCredits);
  }, [currentPlan, usageStats]);

  const getRemainingCareerMaps = useCallback(() => {
    if (!currentPlan || !usageStats) return 0;
    
    const maxMaps = currentPlan.maxCareerMaps;
    const currentMaps = usageStats.careerMapsCreated || 0;
    
    if (maxMaps === -1) return Infinity;
    
    return Math.max(0, maxMaps - currentMaps);
  }, [currentPlan, usageStats]);

  const getRemainingPortfolios = useCallback(() => {
    if (!currentPlan || !usageStats) return 0;
    
    const maxPortfolios = currentPlan.maxPortfolios;
    const currentPortfolios = usageStats.portfoliosCreated || 0;
    
    if (maxPortfolios === -1) return Infinity;
    
    return Math.max(0, maxPortfolios - currentPortfolios);
  }, [currentPlan, usageStats]);

  const getRestrictionMessage = useCallback((restrictionType) => {
    const planName = currentPlan?.name || 'Free';
    
    const messages = {
      career_map: `You don't have access to this career map in your current ${planName} plan. Upgrade to access more career paths!`,
      career_map_limit: `You've reached the limit of career maps for your ${planName} plan. Upgrade to create more!`,
      portfolio: `You've reached the limit of portfolios for your ${planName} plan. Upgrade to create more!`,
      professional_profile: `Professional profiles are not available in your ${planName} plan. Upgrade to Pro to access!`,
      fresher_profile: `Fresher profiles are not available in your ${planName} plan. Upgrade to access!`,
      mentor_credits: `You've used all your mentor credits. Upgrade your plan to get more credits!`,
      job_profiles: `Job profiles are not available in your ${planName} plan. Upgrade to access!`,
      aptitude: `Aptitude tests are not available in your ${planName} plan. Upgrade to access!`,
      scorecard: `Scorecard feature is not available in your ${planName} plan. Upgrade to Pro to access!`,
      examinations: `Examinations are not available in your ${planName} plan. Upgrade to access!`,
      industry_outlook: `Industry outlook is not available in your ${planName} plan. Upgrade to Pro to access!`,
      hard_skills: `Hard skills tracking is not available in your ${planName} plan. Upgrade to Pro to access!`,
      explore_opportunities: `Explore opportunities is not available in your ${planName} plan. Upgrade to Pro to access!`,
      cart: `Cart feature is not available in your ${planName} plan. Upgrade to access!`,
      notes: `Notes feature is not available in your ${planName} plan. Upgrade to access!`,
      courses_preparation: `Courses & Preparation is not available in your ${planName} plan. Upgrade to access!`,
      connect_mentor: `Connect with mentors is not available in your ${planName} plan. Upgrade to access mentor guidance!`,
      view_others_portfolio: `Viewing other students' portfolios is not available in your ${planName} plan. Upgrade to access!`,
    };
    
    return messages[restrictionType] || `This feature is not available in your ${planName} plan. Upgrade to access!`;
  }, [currentPlan]);

  // Career Map Restriction Methods
  const getAllowedCareerCategories = useCallback(() => {
    return currentPlan?.features?.allowed_career_categories || [];
  }, [currentPlan]);

  const getAllowedCareerSubcategories = useCallback(() => {
    return currentPlan?.features?.allowed_career_subcategories || [];
  }, [currentPlan]);

  const canAccessCareerCategory = useCallback((categorySlug) => {
    const allowedCategories = getAllowedCareerCategories();
    return canAccessCategory(allowedCategories, categorySlug);
  }, [getAllowedCareerCategories]);

  const canAccessCareerSubcategory = useCallback((categorySlug, subcategorySlug) => {
    const allowedCategories = getAllowedCareerCategories();
    const allowedSubcategories = getAllowedCareerSubcategories();
    return canAccessSubcategory(allowedCategories, allowedSubcategories, categorySlug, subcategorySlug);
  }, [getAllowedCareerCategories, getAllowedCareerSubcategories]);

  const filterCareerCategories = useCallback((categories) => {
    const allowedCategories = getAllowedCareerCategories();
    return filterCategories(categories, allowedCategories);
  }, [getAllowedCareerCategories]);

  const filterCareerSubcategories = useCallback((subcategories, categorySlug) => {
    const allowedCategories = getAllowedCareerCategories();
    const allowedSubcategories = getAllowedCareerSubcategories();
    return filterSubcategories(subcategories, categorySlug, allowedCategories, allowedSubcategories);
  }, [getAllowedCareerCategories, getAllowedCareerSubcategories]);

  const getCareerCategoryRestrictionInfo = useCallback((categorySlug) => {
    const allowedCategories = getAllowedCareerCategories();
    return getCategoryRestrictionInfo(categorySlug, allowedCategories);
  }, [getAllowedCareerCategories]);

  const getCareerSubcategoryRestrictionInfo = useCallback((categorySlug, subcategorySlug) => {
    const allowedCategories = getAllowedCareerCategories();
    const allowedSubcategories = getAllowedCareerSubcategories();
    return getSubcategoryRestrictionInfo(categorySlug, subcategorySlug, allowedCategories, allowedSubcategories);
  }, [getAllowedCareerCategories, getAllowedCareerSubcategories]);

  const hasFullCareerMapAccess = useCallback(() => {
    const allowedCategories = getAllowedCareerCategories();
    const allowedSubcategories = getAllowedCareerSubcategories();
    return hasFullAccess(allowedCategories, allowedSubcategories);
  }, [getAllowedCareerCategories, getAllowedCareerSubcategories]);

  const getCareerMapAccessDescription = useCallback(() => {
    const allowedCategories = getAllowedCareerCategories();
    const allowedSubcategories = getAllowedCareerSubcategories();
    return getAccessLevelDescription(allowedCategories, allowedSubcategories);
  }, [getAllowedCareerCategories, getAllowedCareerSubcategories]);

  return {
    currentPlan,
    usageStats,
    loading,
    error,
    refreshPlanData,
    canAccessFeature,
    canCreateCareerMap,
    canCreatePortfolio,
    canAccessProfileType,
    hasMentorCredits,
    getRemainingMentorCredits,
    getRemainingCareerMaps,
    getRemainingPortfolios,
    getRestrictionMessage,
    // Career Map Restrictions
    getAllowedCareerCategories,
    getAllowedCareerSubcategories,
    canAccessCareerCategory,
    canAccessCareerSubcategory,
    filterCareerCategories,
    filterCareerSubcategories,
    getCareerCategoryRestrictionInfo,
    getCareerSubcategoryRestrictionInfo,
    hasFullCareerMapAccess,
    getCareerMapAccessDescription,
  };
};

export default usePlanRestrictions;
